import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { AvatarName, AvatarNameCounter, AvatarNameGroup, Tooltip } from "@similarweb/ui-components";

import { buildAvatarNamePreview } from "app/pages/sales-intelligence/domain/teams/utils/buildAvatarNamePreview";
import { coversEveryTeam } from "app/pages/sales-intelligence/domain/teams/utils/coversEveryTeam";
import { useTeamsListQuery } from "app/pages/sales-intelligence/modules/teams/queries/useTeamsListQuery";

import { getTeamAvatarInitial, resolveScoringRuleTeamNames } from "../../utils/resolveScoringRuleTeamNames";

import { StyledAllTeamsAvatarCircle } from "./styles";

type Props = {
    teamIds?: readonly number[];
};

export const ScoringRuleTeamsPreview = ({ teamIds }: Props) => {
    const { translate } = useTranslate();
    const { data: teams = [], isSuccess } = useTeamsListQuery();

    const teamNames = useMemo(() => resolveScoringRuleTeamNames(teamIds, teams), [teamIds, teams]);
    const { firstName, remainingNames } = useMemo(
        () => buildAvatarNamePreview(teamNames, teamNames.length, null),
        [teamNames],
    );
    const isEveryTeam = useMemo(
        () =>
            coversEveryTeam(
                teamIds ?? [],
                teams.map((team) => team.teamId),
            ),
        [teamIds, teams],
    );

    // A rule without teams reaches the whole account rather than a set of teams, so it has no avatars.
    if (!isSuccess || !teamIds?.length) {
        return null;
    }

    if (isEveryTeam) {
        return (
            <Tooltip
                placement="top"
                content={translate("si.pages.score_settings.card.teams.all.tooltip")}
            >
                <StyledAllTeamsAvatarCircle data-automation="scoring-rule-card.all-teams-preview">
                    {translate("si.pages.score_settings.card.teams.all.label")}
                </StyledAllTeamsAvatarCircle>
            </Tooltip>
        );
    }

    if (firstName === undefined) {
        return null;
    }

    return (
        <AvatarNameGroup data-automation="scoring-rule-card.teams-preview">
            <AvatarName
                name={getTeamAvatarInitial(firstName)}
                tooltipName={firstName}
            />
            <AvatarNameCounter names={remainingNames} />
        </AvatarNameGroup>
    );
};
